import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Eye, Loader2, Search as SearchIcon, RefreshCw, Calendar } from 'lucide-react';
import FilterBar from './FilterBar';
import VisitDetailsModal from './VisitDetailsModal';
import StaffDateGate, { gateScopeParams } from './StaffDateGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 100;

/**
 * Visit Reports (v2.5.0 — renamed from "Visit Approvals").
 *
 * Approval workflow retired — visits go straight to `completed` and
 * claims are settled from reports manually. This page is now a
 * **read-only report** with these guarantees:
 *
 *   • Defaults to TODAY only (no bulk fetch on mount).
 *   • From/To date + staff pick required. "Load" click before any
 *     data leaves the DB.
 *   • Shows all completed visits in-range (regardless of approval
 *     status) so historical rows stay visible.
 *   • No approve / reject buttons. Row click → visit details modal
 *     with the same map + KM + expense as before.
 */
export default function VisitApprovals() {
  const today = new Date().toISOString().slice(0, 10);
  const [visits, setVisits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [gate, setGate] = useState({
    geo_fence_id: null, geo_fence_name: null, staff: null,
    from: today, to: today,
  });
  const [openVisitId, setOpenVisitId] = useState(null);

  const fetchPage = useCallback(async (skip) => {
    const scope = gateScopeParams(gate);
    if (!scope) return { data: [], total: 0 };
    const token = localStorage.getItem('token');
    // v2.5.0 — no status filter so the report shows the full picture
    // for the picked staff + window, not just one workflow state.
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(PAGE_SIZE),
      ...scope,
    });
    const r = await fetch(`${API_URL}/api/admin/visits?${params.toString()}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = r.ok ? await r.json() : [];
    return {
      data,
      total: parseInt(r.headers.get('X-Total-Count') || data.length, 10),
    };
  }, [gate.geo_fence_id, gate.staff?.id, gate.from, gate.to]);

  const applyGate = async () => {
    setLoading(true); setHasLoaded(true);
    try {
      const { data, total } = await fetchPage(0);
      setVisits(data);
      setTotal(total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (loadingMore || visits.length >= total) return;
    setLoadingMore(true);
    try {
      const { data } = await fetchPage(visits.length);
      if (data.length) setVisits(prev => [...prev, ...data]);
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return visits.filter(v => {
      if (vehicleFilter !== 'all' && (v.vehicle_type || 'own') !== vehicleFilter) return false;
      if (dateRange.from || dateRange.to) {
        const t = v.start?.time || v.created_at;
        if (!t) return false;
        const day = new Date(t).toISOString().slice(0, 10);
        if (dateRange.from && day < dateRange.from) return false;
        if (dateRange.to && day > dateRange.to) return false;
      }
      if (q && !(
        (v.staff_name || '').toLowerCase().includes(q) ||
        (v.supplier_name || '').toLowerCase().includes(q) ||
        (v.purpose || '').toLowerCase().includes(q)
      )) return false;
      return true;
    });
  }, [visits, search, vehicleFilter, dateRange]);

  const remaining = Math.max(0, total - visits.length);
  const hasMore = remaining > 0;

  return (
    <div data-testid="visit-approvals">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Visit Reports</h1>
          <p className="text-xs text-gray-500 mt-1">
            Read-only report of planned staff visits. Approval workflow retired — claims are settled manually from the visit details.
          </p>
        </div>
        {hasLoaded && (
          <div className="text-xs text-gray-500 flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-full px-3 py-1" data-testid="visit-report-count">
            <Calendar size={12} />
            <span><strong className="text-gray-800">{filtered.length}</strong> of <strong className="text-gray-800">{total}</strong> visits in range</span>
          </div>
        )}
      </div>

      <div className="my-4">
        <StaffDateGate value={gate} onChange={setGate} onApply={applyGate} loading={loading} defaultDays={1} />
      </div>

      {!hasLoaded && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center text-blue-700" data-testid="visit-approvals-prompt">
          <SearchIcon className="mx-auto mb-2 opacity-60" size={28} />
          <p className="font-medium">Pick a staff member and date range, then tap <strong>Load</strong>.</p>
          <p className="text-xs text-blue-600 mt-1">Loads today by default — narrow or widen the window to see more.</p>
        </div>
      )}

      {hasLoaded && (
        <FilterBar
          testId="visit-approval-filters"
          placeholder="Search by staff, supplier or purpose…"
          search={search}
          onSearch={setSearch}
          chips={[
            { key: 'vehicle', label: 'Vehicle', value: vehicleFilter, options: [
              { value: 'own', label: 'Own' }, { value: 'company', label: 'Company' }
            ]}
          ]}
          onChipChange={(_, v) => setVehicleFilter(v)}
          dateRange={dateRange}
          onDateChange={setDateRange}
          onClear={() => { setSearch(''); setVehicleFilter('all'); setDateRange({ from: '', to: '' }); }}
          resultCount={filtered.length}
        />
      )}

      {hasLoaded && (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border" data-testid="visit-approval-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">Staff</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">Supplier(s) / Purpose</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">Status</th>
                <th className="px-3 py-2 text-right text-xs font-medium uppercase">KM</th>
                <th className="px-3 py-2 text-right text-xs font-medium uppercase">Claim</th>
                <th className="px-3 py-2 text-right text-xs font-medium uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 && !loading && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No visits in this range.</td></tr>
              )}
              {loading && filtered.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500"><Loader2 className="inline animate-spin mr-2" size={14} />Loading…</td></tr>
              )}
              {filtered.map(v => {
                const date = (v.start?.time || v.created_at || '').slice(0, 10);
                return (
                  <tr
                    key={v.id}
                    className="hover:bg-blue-50 cursor-pointer"
                    onClick={() => setOpenVisitId(v.id)}
                    data-testid={`visit-row-${v.id}`}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">{date}</td>
                    <td className="px-3 py-2 whitespace-nowrap font-medium">{v.staff_name || '—'}</td>
                    <td className="px-3 py-2 truncate max-w-[280px]">
                      <span className="font-medium">{v.supplier_name || '—'}</span>
                      {v.purpose && <span className="text-gray-500"> · {v.purpose}</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <StatusPill status={v.status} />
                    </td>
                    <td className="px-3 py-2 text-right">{v.distance_km || 0}</td>
                    <td className="px-3 py-2 text-right font-semibold text-emerald-700">₹{v.expense?.amount || 0}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setOpenVisitId(v.id)}
                        className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                        title="View details"
                        data-testid={`view-visit-${v.id}`}
                      ><Eye size={16} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {hasLoaded && hasMore && (
        <div className="flex justify-center mt-6">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-full shadow-sm disabled:opacity-60"
            data-testid="visit-approvals-load-more"
          >
            {loadingMore ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {loadingMore ? 'Loading…' : `Load more (${remaining} remaining)`}
          </button>
        </div>
      )}

      {openVisitId && (
        <VisitDetailsModal visitId={openVisitId} onClose={() => setOpenVisitId(null)} />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || '').replace(/_/g, ' ');
  const cls = ({
    completed: 'bg-emerald-100 text-emerald-700',
    completed_pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    ongoing: 'bg-blue-100 text-blue-700',
    cancelled: 'bg-gray-100 text-gray-600',
  })[status] || 'bg-gray-100 text-gray-600';
  return <span className={`inline-block text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-medium ${cls}`}>{s || '—'}</span>;
}
